import React, { type CSSProperties, useState, useRef } from "react";
import "./GroupComponent.styles.scss";
import moment from "moment";

import Card from "../../atoms/CardV2/Card";
import Badge from "../../atoms/Badge/Badge";
import Spinner from "../../atoms/Spinner/Spinner";
import Button from "../../atoms/Button/Button";
import SideDrawer from "../../atoms/SideDrawer/SideDrawer";
import OcrPdfViewer from "../OcrPdfViewer/OcrPdfViewer";

import { getEncryptedBody, getDecryptedData } from "../../utils/common";
import { toast } from "react-toastify";
import {
  RiArrowRightUpLine,
  RiFileCopyFill,
  RiFileTextFill,
  RiInformationFill,
} from "react-icons/ri";
import { getLabelFromKey } from "../../helpers";
import environment from "../../constants/environments";

export type GroupComponentType = {
  fRAMEFRAMEPlaceholder:
    | "Doctor Prescription"
    | "Discharge Summary"
    | "Purchase Invoice";
  propWidth?: CSSProperties["width"];
  pdfUrl: string;
  recordData?: any;
  onApprove: (recordData?: any) => void;
  isApproved: boolean;
  onReupload: (recordData?: any) => void;
  isReupload: boolean;
  patientId?: string;
  visitId?: string;
  /** Called after a successful file upload (e.g. parent refetches visit records). */
  onUploadSuccess?: () => void;
  enableInsightsApply?: boolean;
  handleApply?: (data: any) => void;
  loading?: boolean;
};

const GroupComponent: React.FC<GroupComponentType> = ({
  fRAMEFRAMEPlaceholder,
  propWidth,
  pdfUrl,
  recordData,
  onApprove,
  isApproved,
  onReupload,
  isReupload,
  // isReupload
  patientId,
  visitId,
  onUploadSuccess,
  enableInsightsApply = false,
  handleApply,
  loading = false,
}) => {
  const [showAiInsightsSideDrawer, setShowAiInsightsSideDrawer] =
    useState<boolean>(false);
  const canUpload = !pdfUrl || isReupload;
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const UploadFileAPI = `${process.env.REACT_APP_API_URL}/api/v8/patient/updated_records`;

  console.log("recordData", recordData);

  const ocrResponse = recordData?.record_meta_info?.ocr;

  const openPDFPreviewInNewTab = (pdfUrl: string) => {
    window.open(pdfUrl, "_blank");
  };

  const handleUploadFile = async (file?: File | null) => {
    // Allows selecting a file from device and uploading it
    if (!file) return;
    try {
      const token = localStorage.getItem("token");
      const secretKey = process.env.REACT_APP_HEALTH_SECRET_KEY!;
      const formData = new FormData();
      formData.append("file", file);
      const extensionType = file.type !== "application/pdf" ? "jpg" : "pdf";
      const payload = {
        ...(isReupload && {
          test_type_id: recordData?.test_type_id,
          patient_records_id: recordData?.patient_records_id,
        }),
        title:
          recordData?.title ||
          (fRAMEFRAMEPlaceholder === "Doctor Prescription"
            ? "Prescriptions"
            : fRAMEFRAMEPlaceholder),
        test_name:
          (recordData?.title?.toLowerCase()?.includes("purchase invoice")
            ? "Invoice"
            : recordData?.title) ||
          (fRAMEFRAMEPlaceholder === "Doctor Prescription"
            ? "Prescriptions"
            : fRAMEFRAMEPlaceholder === "Purchase Invoice"
            ? "Invoice"
            : "Discharge Summary"),
        container_account: environment.AZURE_STORAGE_ACCOUNT,
        document_data: [
          isReupload
            ? recordData?.document_name || ""
            : `${
                patientId || recordData?.patient_id
              }-New-Record-${moment().unix()}.pdf`,
        ],
        container_name: recordData?.container_name || "patient-reports",
        extension_type: extensionType,
        meta_data: {
          report_status: "Under Review",
          onco_cycle_report: "1",
          // ...(document?.visit_id && { visit_id: document?.visit_id }),
          ...(visitId && { visit_id: visitId }),
        },
        patient_id: patientId || recordData?.patient_id,
      };

      const enCryptedBody = getEncryptedBody(payload);
      formData.append("payload", enCryptedBody);

      const response = await fetch(UploadFileAPI, {
        method: "POST",
        headers: {
          token: `${token}`,
          health_secret: secretKey,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch PDF URL");
      }
      const data = await response.json();

      const decryptedData = getDecryptedData(data);
      let parsed: any = null;
      try {
        parsed = JSON.parse(decryptedData);
      } catch (e) {
        // keep parsed as null
      }

      const isSuccess = parsed ? Number(parsed?.code) === 1 : true;
      if (isSuccess) {
        toast.success(parsed?.message || "Record uploaded successfully");
        onUploadSuccess?.();
        window.dispatchEvent(new Event("verifyDocumentsRefresh"));
      } else {
        toast.error(parsed?.message || "Failed to upload record");
      }
      setIsUploading(false);
    } catch (error) {
      console.error("Error fetching PDF URL:", error);
      toast.error("Failed to upload record");
      setIsUploading(false);
    }
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleApprove = () => {
    onApprove(recordData);
  };

  const handleReupload = () => {
    onReupload(recordData);
  };

  const renderUpload = () => {
    return (
      <>
        <Button
          variant="outlined"
          onClick={handlePickFile}
          disabled={!canUpload}
          //style={{ width: "48%" }}
          loading={isUploading}
        >
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setIsUploading(true);
            handleUploadFile(file);
            // allow selecting the same file again
            e.currentTarget.value = "";
          }}
        />
      </>
    );
  };

  const getFooterComponent = () => {
    if (recordData?.record_meta_info?.report_status === "Under Review") {
      return (
        <>
          <Button onClick={() => openPDFPreviewInNewTab(pdfUrl)}>
            View Document
          </Button>

          {recordData?.record_meta_info?.ocr?.data?.status === "success" && (
            <Button
              variant="outlined"
              onClick={() => setShowAiInsightsSideDrawer(true)}
            >
              <RiArrowRightUpLine size={24} />
              <span>View Analysis</span>
            </Button>
          )}

          <Button onClick={handleApprove} disabled={!pdfUrl}>
            Approve
          </Button>
          <Button
            variant="outlined"
            onClick={handleReupload}
            disabled={!pdfUrl}
          >
            Request to upload again
          </Button>
        </>
      );
    } else if (recordData?.record_meta_info?.report_status === "Approved") {
      return (
        <>
          <Button onClick={() => openPDFPreviewInNewTab(pdfUrl)}>
            View Document
          </Button>

          {recordData?.record_meta_info?.ocr?.data?.status === "success" && (
            <Button
              variant="outlined"
              onClick={() => setShowAiInsightsSideDrawer(true)}
            >
              <RiArrowRightUpLine size={24} />
              <span>View Analysis</span>
            </Button>
          )}
        </>
      );
    } else if (recordData?.record_meta_info?.report_status === "Rejected") {
      return <>{renderUpload()}</>;
    } else {
      return <>{renderUpload()}</>;
    }
  };

  return (
    <Card
      titleIcon={<RiFileTextFill size={24} />}
      title={fRAMEFRAMEPlaceholder}
      actionComponent={
        !loading && (
          <Badge
            variant={
              recordData?.record_meta_info?.report_status === "Under Review"
                ? "warning"
                : recordData?.record_meta_info?.report_status === "Approved"
                ? "success"
                : recordData?.record_meta_info?.report_status === "Rejected"
                ? "danger"
                : "info"
            }
          >
            {recordData?.record_meta_info?.report_status === "Under Review"
              ? "Pending"
              : recordData?.record_meta_info?.report_status === "Approved"
              ? "Approved"
              : recordData?.record_meta_info?.report_status === "Rejected"
              ? "Rejected"
              : "Upload Pending"}
          </Badge>
        )
      }
      fullWidth={true}
      footer={getFooterComponent()}
    >
      <div className="group-component-content">
        {loading ? (
          <div className="spinner-container">
            <Spinner />
          </div>
        ) : recordData?.record_meta_info?.report_status === "Rejected" ? (
          <div className="rejected-document-container">
            <RiInformationFill size={24} color="#ff4961" />
            <div className="rejected-document-title">Document Rejected</div>
            <div className="rejected-document-reason">
              <b>Reason:</b> {recordData?.record_meta_info?.rejected_reason}
            </div>
          </div>
        ) : pdfUrl ? (
          <OcrResponseContainer
            data={ocrResponse}
            onViewAnalysis={() => setShowAiInsightsSideDrawer(true)}
            onApply={(data) => {
              if (recordData?.record_meta_info?.report_status !== "Approved") {
                toast.error(
                  "Please approve the document first to apply the insights"
                );
                return;
              } else {
                handleApply?.(data);
                toast.success("Insights applied successfully");
              }
            }}
            enableApply={enableInsightsApply}
          />
        ) : (
          <div className="document-upload-container">
            <div className="document-upload-title">No Record Uploaded</div>
          </div>
        )}
      </div>

      {showAiInsightsSideDrawer && (
        <SideDrawer
          isOpen={showAiInsightsSideDrawer}
          title="OCR Insights"
          onClose={() => setShowAiInsightsSideDrawer(false)}
          position="right"
        >
          <OcrPdfViewer
            foundFields={ocrResponse?.data?.found_fields}
            // highlightedUrl={pdfUrl || highlightedUrl}
            highlightedUrl={ocrResponse?.data?.highlighted_url}
          />
        </SideDrawer>
      )}
    </Card>
  );
};

export default GroupComponent;

const OcrResponseContainer: React.FC<{
  data: {
    status: "success" | "failed" | "processing";
    message: string;
    data: {
      summary: string;
      highlighted_url: string;
      found_fields: Record<
        string,
        {
          value: string;
          coordinates: Array<{
            page: number;
            bbox: Array<{ x: number; y: number }>;
          }>;
        }
      >;
    };
  };
  enableApply?: boolean;
  onViewAnalysis?: () => void;
  onApply?: (data: any) => void;
}> = ({ data, onApply, enableApply = false, onViewAnalysis }) => {
  const hightlightKeys = [
    "primary_drug",
    // "primary_drug_dosage",
    "primary_drug_quantity",
    "secondary_drug",
    // "secondary_drug_dosage",
    "secondary_drug_quantity",
    "cancer_type",
    "next_cycle_date",
    "therapy_type",
    "therapy_indication",
    "bill_date",
  ];

  const getHighlightsFromData = (
    foundFields: Record<
      string,
      {
        value: string;
        coordinates: Array<{
          page: number;
          bbox: Array<{ x: number; y: number }>;
        }>;
      }
    >
  ) => {
    if (!foundFields) return null;
    const highlights: Record<
      string,
      {
        value: string;
        coordinates: Array<{
          page: number;
          bbox: Array<{ x: number; y: number }>;
        }>;
      }
    > = {};
    hightlightKeys?.forEach((key) => {
      if (foundFields?.[key]) {
        highlights[key] = foundFields?.[key];
      }
    });
    return highlights;
  };

  const highlights =
    data?.status === "success"
      ? getHighlightsFromData(data?.data?.found_fields)
      : null;

  const handleApply = () => {
    const payload: Record<string, string> = {};

    hightlightKeys?.forEach((key) => {
      payload[key] = highlights?.[key]?.value || "";
    });

    onApply?.(payload);
  };

  return (
    <>
      {data?.status === "success" && highlights ? (
        <div className="ocr-response-content-summary">
          <p className="ocr-response-content-summary-greeting">
            Greetings! Here are the insights for the uploaded document:
          </p>
          <ul>
            {Object.keys(highlights)?.length > 0 &&
              Object.keys(highlights)?.map((key: string) => (
                <li>
                  <b>{getLabelFromKey(key)}</b>: {highlights?.[key]?.value}
                </li>
              ))}
          </ul>

          <div className="action-buttons">
            <button className="action-button" onClick={onViewAnalysis}>
              <RiArrowRightUpLine size={24} />
              <span>View Analysis</span>
            </button>

            {enableApply && (
              <button className="action-button" onClick={handleApply}>
                <RiFileCopyFill size={24} />
              </button>
            )}
          </div>
        </div>
      ) : data?.status === "processing" ? (
        <div className="ocr-response-content-summary">
          <div className="ocr-response-content-summary-spinner">
            <Spinner />
          </div>
          <div className="ocr-response-content-summary-text">
            We are analyzing the document, insights will be available soon...
          </div>
        </div>
      ) : data?.status === "failed" ? (
        <div className="ocr-response-content-summary">
          <div className="ocr-response-content-summary-spinner">
            <RiInformationFill size={24} color="#ff4961" />
          </div>
          <div className="ocr-response-content-summary-text">
            Unable to analyse the document/Insights not available
          </div>
        </div>
      ) : null}
    </>
  );
};
